import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFleetStore } from '@/stores/fleet';
import { useEventsStore, type FeedEvent } from '@/stores/events';
import { rentalsApi } from '@/api/endpoints/rentals';
import { geofencesApi } from '@/api/endpoints/geofences';

/** Hash estável (negativo, p/ não colidir com ids do Traccar). */
function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return -Math.abs(h) - 1;
}
const pad = (n: number) => String(n).padStart(2, '0');

const VENC_AVISO_DIAS = 30; // avisa quando faltam ≤30 dias

/**
 * Gera alertas náuticos derivados (não vêm do Traccar) e injeta no feed:
 *  - retorno de locação atrasado
 *  - seguro/documento vencido ou perto do vencimento
 *  - saída de geocerca do tipo "marina"
 * Dedup estável por dia (1 alerta/dia por item) via id+serverTime fixos.
 */
export function useDerivedAlerts() {
  const devices = useFleetStore((s) => s.devices);
  const rentalsQ = useQuery({ queryKey: ['rentals'], queryFn: () => rentalsApi.list(), staleTime: 300_000 });
  const geofencesQ = useQuery({ queryKey: ['geofences'], queryFn: () => geofencesApi.list(), staleTime: 300_000 });

  useEffect(() => {
    const run = () => {
      const now = Date.now();
      const d = new Date();
      const today = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const stamp = `${today}T08:00:00`;
      const synth: Omit<FeedEvent, 'uid' | 'read'>[] = [];

      // 1) Locações atrasadas
      for (const r of rentalsQ.data ?? []) {
        if (r.finalizada || !r.retorno_previsto_at) continue;
        if (now > new Date(r.retorno_previsto_at).getTime()) {
          const key = `rentalOverdue:${r.id}:${today}`;
          synth.push({
            id: hashId(key), type: 'rentalOverdue', serverTime: stamp,
            deviceId: r.device_id, attributes: { cliente: r.cliente, retorno: r.retorno_previsto_at },
          });
        }
      }

      // 2) Vencimentos de documento/seguro (atributos da embarcação)
      const fields: [string, 'doc' | 'insurance'][] = [['vencDoc', 'doc'], ['vencSeguro', 'insurance']];
      for (const dev of devices.values()) {
        const a = dev.attributes ?? {};
        for (const [attr, kind] of fields) {
          const v = a[attr];
          if (typeof v !== 'string' || !v) continue;
          const t = new Date(`${v}T00:00:00`).getTime();
          if (Number.isNaN(t)) continue;
          const dias = Math.floor((t - now) / 86_400_000);
          let type: string | null = null;
          if (dias < 0) type = kind === 'doc' ? 'docExpired' : 'insuranceExpired';
          else if (dias <= VENC_AVISO_DIAS) type = kind === 'doc' ? 'docExpiring' : 'insuranceExpiring';
          if (!type) continue;
          const key = `${type}:${dev.id}:${today}`;
          synth.push({ id: hashId(key), type, serverTime: stamp, deviceId: dev.id, attributes: { venc: v, dias } });
        }
      }

      // 3) Saída de marina (a partir de eventos geofenceExit já no feed)
      const marinaIds = new Set(
        (geofencesQ.data ?? [])
          .filter((g) => (g.attributes?.tipo as string | undefined) === 'marina')
          .map((g) => g.id),
      );
      if (marinaIds.size > 0) {
        for (const e of useEventsStore.getState().items) {
          if (e.type !== 'geofenceExit' || !e.geofenceId || !marinaIds.has(e.geofenceId)) continue;
          if (now - new Date(e.serverTime).getTime() > 86_400_000) continue; // só últimas 24h
          const key = `marinaExit:${e.uid}`;
          synth.push({
            id: hashId(key), type: 'marinaExit', serverTime: e.serverTime,
            deviceId: e.deviceId, geofenceId: e.geofenceId, attributes: {},
          });
        }
      }

      if (synth.length) useEventsStore.getState().push(synth);
    };

    run();
    const t = setInterval(run, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [devices, rentalsQ.data, geofencesQ.data]);
}
